import AddIcon from "@mui/icons-material/Add";
import EuroIcon from "@mui/icons-material/Euro";
import PercentIcon from "@mui/icons-material/Percent";
import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	ProjectSaleableService,
	ProjectSaleableServiceGroup,
	ProjectSaleableUnit,
	useCreateSaleableServiceMutation,
	useGetAllSaleableUnitsLazyQuery,
	useUpdateSaleableServiceMutation,
} from "@repo/server-api";
import { OptionType } from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import DialogServiceGroup from "./DialogServiceGroup";
import DialogServiceUnit from "./DialogServiceUnit";
import NumberTextField from "../fields/NumberTextField";
import Dropdown from "../sections/Dropdown";

export default function DialogService(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		service: ProjectSaleableService | null;
		groups: ProjectSaleableServiceGroup[];
		setGroups: (value: ProjectSaleableServiceGroup[]) => void;
	}>,
) {
	const { t } = useTranslation();

	const open = useMemo(() => props.open, [props.open]);
	const [service, setService] = useState<ProjectSaleableService | null>(null);
	const [group, setGroup] = useState<ProjectSaleableServiceGroup | null>(null);
	const [units, setUnits] = useState<ProjectSaleableUnit[]>([]);

	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [optionElementGroup, setOptionElementGroup] = useState<OptionType>({
		title: "",
		inputValue: "",
		item: null,
	});
	const [openDialogServiceUnit, setOpenDialogServiceUnit] = useState<boolean>(false);
	const [openDialogServiceGroup, setOpenDialogServiceGroup] = useState<boolean>(false);
	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [updateService] = useUpdateSaleableServiceMutation({
		variables: {
			groupId: group?.id ?? "",
			id: service?.id ?? "",
			name: service?.name ?? "",
			purchasePrice: service?.purchasePrice ?? 0,
			unitId: service?.unit?.id ?? "",
			sellingPrice: service?.sellingPrice ?? 0,
			vatRate: service?.vatRate ?? 0,
		},
		onCompleted(data) {
			if (data.updateSaleableService.saleableService) {
				props.refreshData();
				handleClose();
			}
		},
	});

	const [createService] = useCreateSaleableServiceMutation({
		variables: {
			groupId: group?.id ?? "",
			name: service?.name ?? "",
			purchasePrice: service?.purchasePrice ?? 0,
			unitId: service?.unit?.id ?? "",
			sellingPrice: service?.sellingPrice ?? 0,
			vatRate: service?.vatRate ?? 0,
		},
		onCompleted(data) {
			if (data.createSaleableService.saleableService) {
				props.refreshData();
				handleClose();
			}
		},
	});

	const [getAllUnits] = useGetAllSaleableUnitsLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data && data.allSaleableUnits) {
				const tmp = data.allSaleableUnits.map((unit) => {
					return {
						...(unit as ProjectSaleableUnit),
					};
				});
				setUnits(tmp);
			}
		},
	});

	useEffect(() => {
		getAllUnits();
	}, []);

	useEffect(() => {
		if (props.service) {
			setService({
				...props.service,
			});
			const group =
				props.groups.find((g) => g.services.find((s) => s.id === props.service?.id)) ?? null;
			setGroup(group);
			setOptionElementGroup({
				title: group?.name ?? "",
				inputValue: group?.name ?? "",
				item: group ?? null,
			});
		}
	}, [props.service]);

	const sellingPriceRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const purchasePriceRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const vatRateRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validationServiceSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
		unit: Yup.object().shape({
			id: Yup.string().required(t("validation.unit.required")),
		}),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationServiceSchema.validate(service, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		localErrors.purchasePrice = (await purchasePriceRef.current?.validate()) || {};
		localErrors.sellingPrice = (await sellingPriceRef.current?.validate()) || {};
		localErrors.vatRate = (await vatRateRef.current?.validate()) || {};

		// TODO Find better solution
		// Problem is because errors.unit is not the same
		// first is unit.id and secont is unit = (id: value)
		localErrors.unit = {
			id: service?.unit?.id ? "" : t("validation.unit.required"),
		};
		localErrors.group = { id: group?.id ? "" : t("validation.group.required") };

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleClose = () => {
		props.setOpen(false);
		setErrors({});
		setService(props.service);
		setSaveClicked(false);
	};

	const handleSubmit = async () => {
		const isValid = await validateFields();
		if (isValid) {
			setSaveClicked(true);
			if (service?.id) {
				updateService();
			} else {
				createService();
			}
			return;
		}
	};

	return (
		<Dialog open={open} maxWidth="sm" fullWidth={true} data-testid="service-dialog">
			<DialogTitle>
				{t("services.dialog.title") + " " + (props.service?.name ? props.service.name : "")}
			</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<Box className="fullCenter">
					<TextField
						onChange={(event) => {
							setService(service ? { ...service, name: event.target.value ?? "" } : null);
							setErrors({ ...errors, name: "" });
						}}
						error={errors.name ? true : false}
						helperText={errors.name as string}
						className="w-100"
						value={service?.name ?? ""}
						label={t("name")}
						variant="outlined"
					/>
				</Box>
				<Box sx={{ width: "100%" }}>
					<Box className="fullCenter">
						<Dropdown
							element={optionElementGroup}
							setElement={(o) => {
								setOptionElementGroup({
									title: o?.title ?? "",
									inputValue: o?.inputValue ?? "",
									item: o?.item ?? null,
								});
								setGroup(o?.item as ProjectSaleableServiceGroup);
								setErrors({ ...errors, group: { id: "" } });
							}}
							elements={props.groups}
							error={errors?.group?.id as string}
							setError={(value) => setErrors({ ...errors, group: { id: value } })}
							searchPlaceholder={t("search-for-service-group", {
								ns: "service",
							})}
							searchLabel={t("service-group")}
							noData={t("no-service-groups-found")}
							add={() => {
								setGroup({
									id: "",
									name: "",
									hasFixSellingPrice: false,
									originalId: "",
									sellingPrice: 0,
									budget: {
										amount: 0,
										hasAmountLimit: false,
										hasTimeLimit: false,
										hours: 0,
									},
									services: [],
								});
								setOpenDialogServiceGroup(true);
							}}
							addModify={() => {
								const updatedItem = {
									id: "",
									name: group?.name ?? "",
									hasFixSellingPrice: false,
									originalId: "",
									sellingPrice: group?.sellingPrice ?? 0,
									budget: {
										amount: 0,
										hasAmountLimit: false,
										hasTimeLimit: false,
										hours: 0,
									},
									services: [],
								};
								setGroup(updatedItem);
								setOpenDialogServiceGroup(true);
							}}
							button={
								<Button
									className="blueButton left"
									variant="contained"
									onClick={() => {
										setGroup({
											id: "",
											name: optionElementGroup?.inputValue ?? "",
											hasFixSellingPrice: false,
											originalId: "",
											sellingPrice: 0,
											budget: {
												amount: 0,
												hasAmountLimit: false,
												hasTimeLimit: false,
												hours: 0,
											},
											services: [],
										});
										setOpenDialogServiceGroup(true);
									}}>
									<AddIcon /> {t("add")}
								</Button>
							}>
							<DialogServiceGroup
								open={openDialogServiceGroup}
								service={group}
								setOpen={setOpenDialogServiceGroup}
								refreshData={props.refreshData}
							/>
						</Dropdown>
					</Box>
				</Box>
				<Box className="flex-between">
					<Box className="w-45">
						<NumberTextField
							isLoaded={true}
							ref={purchasePriceRef}
							label={t("purchase-price")}
							value={service?.purchasePrice ?? 0}
							setValue={(value) => {
								setService(service ? { ...service, purchasePrice: value ?? 0 } : null);
							}}
							icon={<EuroIcon />}
							decimalPlaces={2}
							errors={errors.purchasePrice as Record<string, string>}
							setErrors={(value) => setErrors({ ...errors, purchasePrice: value })}
						/>
					</Box>
					<Box className="w-45">
						<NumberTextField
							isLoaded={true}
							ref={sellingPriceRef}
							label={t("selling-price")}
							value={service?.sellingPrice ?? 0}
							setValue={(value) => {
								setService(service ? { ...service, sellingPrice: value ?? 0 } : null);
							}}
							icon={<EuroIcon />}
							decimalPlaces={2}
							errors={errors.sellingPrice as Record<string, string>}
							setErrors={(value) => setErrors({ ...errors, sellingPrice: value })}
						/>
					</Box>
				</Box>
				<Box className="w-100">
					<NumberTextField
						isLoaded={true}
						ref={vatRateRef}
						label={t("vat-rate")}
						value={service?.vatRate ?? 0}
						setValue={(value) => {
							setService(service ? { ...service, vatRate: value ?? 0 } : null);
						}}
						icon={<PercentIcon />}
						decimalPlaces={3}
						minValue={0}
						maxValue={100}
						errors={errors.vatRate as Record<string, string>}
						setErrors={(value) => setErrors({ ...errors, vatRate: value })}
					/>
				</Box>
				<Box sx={{ width: "100%" }}>
					<Box className="fullCenter">
						<Dropdown
							element={{
								title: service?.unit?.name ?? "",
								inputValue: service?.unit?.name ?? "",
								item: service?.unit ?? null,
							}}
							setElement={(o: OptionType | null) => {
								setService(
									service
										? {
												...service,
												unit: {
													id: (o?.item as ProjectSaleableUnit)?.id ?? "",
													name: o?.inputValue ?? "",
													originalId: (o?.item as ProjectSaleableUnit)?.originalId ?? "",
													shortCode: (o?.item as ProjectSaleableUnit)?.shortCode ?? "",
												},
											}
										: null,
								);
								setErrors({ ...errors, unit: { id: "" } });
							}}
							elements={units}
							error={errors?.unit?.id as string}
							setError={(value) => setErrors?.({ ...errors, unit: { id: value } })}
							searchPlaceholder={t("search-for-unit")}
							searchLabel={t("unit")}
							noData={t("no-units-found")}
							button={
								<Button
									className="blueButton left"
									variant="contained"
									onClick={() => setOpenDialogServiceUnit(true)}>
									<AddIcon /> {t("add")}{" "}
								</Button>
							}
							add={() => {
								setService(
									service
										? {
												...service,
												unit: {
													id: "",
													originalId: "",
													name: "",
													shortCode: "",
												},
											}
										: null,
								);
								setOpenDialogServiceUnit(true);
							}}
							addModify={() => {
								const updatedItem = {
									id: "",
									name: service?.unit?.name ?? "",
									originalId: service?.unit?.originalId ?? "",
									shortCode: service?.unit?.shortCode ?? "",
								};
								setService(service ? { ...service, unit: updatedItem } : null);
								setOpenDialogServiceUnit(true);
							}}>
							<DialogServiceUnit
								unit={service?.unit ?? null}
								open={openDialogServiceUnit}
								setOpen={setOpenDialogServiceUnit}
								refreshData={getAllUnits}
							/>
						</Dropdown>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					variant="contained"
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
